import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useQuery } from "convex/react";
import { SITE, allServicePages, servicePagePath, type LiveServiceRow } from "@/lib/site";
import { api, BUSINESS_ID } from "@/lib/convex";
import logo from "@/assets/logo.webp";

export function SiteNav() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  let closeTimer: ReturnType<typeof setTimeout> | undefined;

  // Menyen «Tjenester» foelger tjenestelista i Ascendly: tjenester eieren har
  // lagt til faar en lenke, tjenester han har skrudd av forsvinner. Foer var
  // menyen en fast liste, saa en ny tjeneste var usynlig for alle som ikke
  // fant den nederst paa /priser.
  const live = useQuery(api.bookings.listServicesForBusiness, {
    businessId: BUSINESS_ID,
  }) as LiveServiceRow[] | undefined;
  const servicePages = useMemo(() => allServicePages(live), [live]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openServices = () => {
    if (closeTimer) clearTimeout(closeTimer);
    setServicesOpen(true);
  };
  const scheduleClose = () => {
    closeTimer = setTimeout(() => setServicesOpen(false), 120);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between gap-4 py-2 md:py-3">
          <Link to="/" className="flex items-center gap-3 font-serif text-lg md:text-xl tracking-tight text-foreground">
            <img
              src={logo}
              alt={`${SITE.name} logo`}
              width={320}
              height={320}
              fetchPriority="high"
              decoding="async"
              className="h-28 w-28 md:h-40 md:w-40 object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-bold text-foreground [text-shadow:0_1px_2px_rgba(0,0,0,0.8),0_2px_8px_rgba(0,0,0,0.5)]">
            <Link to="/" className="hover:text-primary transition-colors">Hjem</Link>
            <div className="relative" onMouseEnter={openServices} onMouseLeave={scheduleClose}>
              <button className="flex items-center gap-1 hover:text-primary transition-colors">
                Tjenester <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <div
                className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-all duration-200 ${
                  servicesOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
                }`}
              >
                <div className="min-w-[240px] rounded-xl border border-border bg-popover shadow-luxe p-2">
                  {servicePages.map((s) => (
                    <Link
                      key={s.slug}
                      to={servicePagePath(s) as any}
                      className="block rounded-lg px-3 py-2 text-sm font-semibold text-foreground hover:bg-secondary hover:text-primary transition-colors"
                    >
                      {s.nav}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <a href="/#anmeldelser" className="hover:text-primary transition-colors">Anmeldelser</a>
            <Link to="/faq" className="hover:text-primary transition-colors">FAQ</Link>
            <Link to="/blogg" className="hover:text-primary transition-colors">Blogg</Link>
            <a href="/#kontakt" className="hover:text-primary transition-colors">Kontakt</a>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/book"
              className="hidden md:inline-flex items-center rounded-full bg-primary-strong px-5 py-2.5 text-sm font-medium text-primary-foreground hover:brightness-110 transition-all lift"
            >
              Book time
            </Link>
            <button
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground"
              onClick={() => setOpen(true)}
              aria-label="Åpne meny"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden bg-background/95 backdrop-blur-md overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-3 font-serif text-lg">
                <img src={logo} alt={`${SITE.name} logo`} className="h-20 w-20 object-contain" />
                {SITE.name}
              </span>
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border"
                onClick={() => setOpen(false)}
                aria-label="Lukk meny"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-10 flex flex-col gap-1 text-lg">
              <Link to="/" onClick={() => setOpen(false)} className="py-3 border-b border-border">Hjem</Link>
              <button
                className="flex items-center justify-between py-3 border-b border-border"
                onClick={() => setMobileServicesOpen((v) => !v)}
              >
                Tjenester
                <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileServicesOpen && (
                <div className="flex flex-col pl-4 border-b border-border">
                  {servicePages.map((s) => (
                    <Link
                      key={s.slug}
                      to={servicePagePath(s) as any}
                      onClick={() => setOpen(false)}
                      className="py-3 text-base text-foreground/80"
                    >
                      {s.nav}
                    </Link>
                  ))}
                </div>
              )}
              <a href="/#anmeldelser" onClick={() => setOpen(false)} className="py-3 border-b border-border">Anmeldelser</a>
              <Link to="/faq" onClick={() => setOpen(false)} className="py-3 border-b border-border">FAQ</Link>
              <Link to="/blogg" onClick={() => setOpen(false)} className="py-3 border-b border-border">Blogg</Link>
              <a href="/#kontakt" onClick={() => setOpen(false)} className="py-3 border-b border-border">Kontakt</a>
              <Link
                to="/book"
                onClick={() => setOpen(false)}
                className="mt-8 inline-flex items-center justify-center rounded-full bg-primary-strong px-6 py-3 font-medium text-primary-foreground"
              >
                Book time
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
